import type { LedgerEntry } from '../data/types';
import { round2 } from '../lib/format';

export interface JournalLine {
  date: string;
  fundId: string;
  account: string;
  debit: number;
  credit: number;
  narration: string;
  source: string;
}

const RULES: Partial<
  Record<LedgerEntry['type'], { debit: string; credit: string; narration: string }>
> = {
  ISSUE: {
    debit: 'Cash at bank – applications',
    credit: 'Unitholder capital',
    narration: 'Units issued',
  },
  CALL_PAYMENT: {
    debit: 'Cash at bank – applications',
    credit: 'Unitholder capital',
    narration: 'Capital call received',
  },
  DISTRIBUTION: {
    debit: 'Distributions to unitholders',
    credit: 'Cash at bank – operating',
    narration: 'Distribution paid (net)',
  },
  WITHHOLDING: {
    debit: 'Distributions to unitholders',
    credit: 'Withholding payable – ATO',
    narration: 'Tax withheld from distribution',
  },
  DRP_ISSUE: {
    debit: 'Cash at bank – operating',
    credit: 'Unitholder capital',
    narration: 'Distribution reinvested (DRP)',
  },
  REDEMPTION: {
    debit: 'Unitholder capital',
    credit: 'Cash at bank – operating',
    narration: 'Units redeemed',
  },
  CAPITAL_RETURN: {
    debit: 'Unitholder capital',
    credit: 'Cash at bank – operating',
    narration: 'Capital returned',
  },
  MIGRATION_OPENING: {
    debit: 'Opening balance suspense',
    credit: 'Unitholder capital',
    narration: 'Opening register balance',
  },
};

export function journalLines(entries: LedgerEntry[]): JournalLine[] {
  const grouped = new Map<string, JournalLine[]>();
  for (const entry of entries) {
    const original =
      entry.type === 'ADJUSTMENT' && entry.reversedType ? RULES[entry.reversedType] : undefined;
    const rule = original
      ? {
          debit: original.credit,
          credit: original.debit,
          narration: `Reversal: ${original.narration.toLowerCase()}`,
        }
      : RULES[entry.type];
    if (!rule || entry.amount === 0) continue;
    const key = `${entry.fundId}|${entry.type}|${entry.effectiveDate}|${entry.eventId ?? entry.reference}`;
    const existing = grouped.get(key);
    if (existing) {
      existing[0].debit = round2(existing[0].debit + entry.amount);
      existing[1].credit = round2(existing[1].credit + entry.amount);
      continue;
    }
    const source = `${entry.type}:${entry.eventId ?? entry.reference}`;
    grouped.set(key, [
      {
        date: entry.effectiveDate,
        fundId: entry.fundId,
        account: rule.debit,
        debit: entry.amount,
        credit: 0,
        narration: rule.narration,
        source,
      },
      {
        date: entry.effectiveDate,
        fundId: entry.fundId,
        account: rule.credit,
        debit: 0,
        credit: entry.amount,
        narration: rule.narration,
        source,
      },
    ]);
  }
  return Array.from(grouped.values())
    .flat()
    .sort((left, right) => left.date.localeCompare(right.date));
}

export function accountTotals(lines: JournalLine[]) {
  const totals = new Map<string, { debit: number; credit: number }>();
  for (const line of lines) {
    const current = totals.get(line.account) ?? { debit: 0, credit: 0 };
    totals.set(line.account, {
      debit: round2(current.debit + line.debit),
      credit: round2(current.credit + line.credit),
    });
  }
  return Array.from(totals.entries()).map(([account, value]) => ({ account, ...value }));
}
